<script setup>
import { useAlertDialog } from '@/utils/stores/alertDialog'
import { useLoading } from '@/utils/stores/loading'
import { useProjectStore } from '@/utils/stores/project'

const alertDialogStore = useAlertDialog()

definePage({
  meta: {
    action: 'read',
    subject: 'data-audit-trail',
  },
})

const loadingStore = useLoading()
const projectStore = useProjectStore()
const { projectName } = storeToRefs(projectStore)

const fetchSiteAll = async () => {
  const {
    data,
    execute: fetchSiteAll,
  } = await useApi(createUrl('/sources/sites/all', {
    query: {
      projectId: getProjectId(),
    },
  }))

  if(data) sites.value = [{ id: 0, name: "전체" }, ...data.value]
}

fetchSiteAll()

const sites = ref([])
const subjects = ref([])
const visits = ref([])
const maxCycleId = ref()
const groups = ref([])
const users = ref([])

const filter = ref({
  siteId: "",
  subjectIds: [],
  visits: [],
  groups: [],
  processes: [],
  users: [],
  startDate: "",
  endDate: "",
})

const fetchSubjects = async () => {
  const {
    data,
  } = await useApi(createUrl('/subjects/subject-list', {
    query: {
      siteId: filter.value.siteId,
      projectId: getProjectId(),
    },
  }))

  if(data) {
    subjects.value = data.value.map(item => ({ id: item.id, name: item.name }))
  }
}

const fetchVisitAll = async () => {
  const {
    data,
  } = await useApi(createUrl('/subjects/visits', {
    query: {
      projectId: getProjectId(),
    },
  }))


  if(data) {
    // eslint-disable-next-line camelcase
    visits.value = [...data.value.visits, { cycle_visit_id: "uv", label: "추가방문" }]
    maxCycleId.value = data.value.maxCycleId
  }
}

fetchVisitAll()

// const fetchGroupAll = async () => {
//   const {
//     data,
//   } = await useApi(createUrl('/projects/groups', {
//     query: {
//       projectId: getProjectId(),
//     },
//   }))

//   console.log(data)

//   if(data&&data.value) {
//     groups.value = [...data.value]
//   }
// }
const fetchGroupAll = async () => {
  try {
    const { data, error } = await useApi(
      createUrl('/projects/groups', { query: { projectId: getProjectId() } }),
    )

    if (error?.value) throw error.value

    const payload = data?.value

    // 응답 형태별로 안전하게 배열 추출
    const list =
      Array.isArray(payload) ? payload
        : Array.isArray(payload?.data) ? payload.data
          : Array.isArray(payload?.items) ? payload.items
            : Array.isArray(payload?.rows) ? payload.rows
              : []

    groups.value = list
  } catch (e) {
    console.error('[fetchGroupAll] 실패:', e)
    groups.value = []
  }
}


fetchGroupAll()

const fetchUsers = async () => {
  const {
    data,
  } = await useApi(createUrl('/sources/users', {
    query: {
      projectId: getProjectId(),
    },
  }))


  if(data) {
    users.value = [...data.value]
  }
}

fetchUsers()


const downloadExcel = async () => {
  loadingStore.message = "요청중... 기다려주세요."
  loadingStore.isLoading = true

  const ids = filter.value.subjectIds.map(item => item.id)

  const data = await $api(`/excel/audit-trail`, { method: 'POST',
    // responseType: "blob",
    body: {
      projectId: getProjectId(),
      siteId: filter.value.siteId,
      subjectIds: ids,
      users: filter.value.users,
      visits: filter.value.visits,
      maxCycleId: maxCycleId.value,
      groups: filter.value.groups,
      processes: filter.value.processes,
      startDate: filter.value.startDate,
      endDate: filter.value.endDate,

    },

    // timeout: 5 * 60 * 1000,
    // onResponseError({ response }) {
    //   loadingStore.isLoading = false
    //   snackbarStore.showSnackBar("오류가 발생하였습니다.", "error")
    // },
  })

  // const url = window.URL.createObjectURL(new Blob([data]))
  // const link = document.createElement('a')

  // link.href = url
  // link.setAttribute('download', "export.xlsx")
  // document.body.appendChild(link)
  // link.click()
  // window.URL.revokeObjectURL(url)
  // link.remove()
  loadingStore.isLoading = false
  alertDialogStore.showAlertDialog("요청이 완료되었습니다. 결과는 이메일로 전달됩니다.")
}

watch(()=>filter.value.siteId, (n, o) => {
  filter.value.subjectIds = []
  fetchSubjects()
})
</script>

<template>
  <VCard>
    <VCardText>
      <VRow>
        <VCol
          cols="2"
          class="d-flex align-center"
        >
          {{ $t('Project Name') }}
        </VCol>
        <VCol cols="10">
          {{ projectName }}
        </VCol>
        <VCol
          cols="2"
          class="d-flex align-center"
        >
          {{ $t('Site') }}
        </VCol>
        <VCol cols="10">
          <AppSelect
            v-model="filter.siteId"
            :items="sites"
            item-title="name"
            item-value="id"
            placeholder="전체"
          />
        </VCol>
        <VCol
          cols="2"
          class="d-flex align-center"
        >
          {{ $t('Subject') }}
        </VCol>
        <VCol cols="10">
          <AppCombobox
            v-model="filter.subjectIds"
            :items="subjects"
            item-title="name"
            item-value="id"
            multiple
            placeholder="전체"
            clearable
          />
        </VCol>
        <VCol
          cols="2"
          class="d-flex align-center"
        >
          {{ $t('Visit') }}
        </VCol>
        <VCol cols="10">
          <AppSelect
            v-model="filter.visits"
            :items="visits"
            item-title="label"
            item-value="cycle_visit_id"
            multiple
            placeholder="전체"
            clearable
          />
        </VCol>
        <VCol
          cols="2"
          class="d-flex align-center"
        >
          {{ $t('CRF group') }}
        </VCol>
        <VCol cols="10">
          <AppSelect
            v-model="filter.groups"
            :items="groups"
            item-title="label"
            item-value="id"
            multiple
            placeholder="전체"
            clearable
          />
        </VCol>
        <VCol
          cols="2"
          class="d-flex align-center"
        >
          과정
        </VCol>
        <VCol cols="10">
          <AppSelect
            v-model="filter.processes"
            :items="['insert', 'update', 'delete']"
            multiple
            chips
            placeholder="전체"
            closable-chips
          />
        </VCol>
        <VCol
          cols="2"
          class="d-flex align-center"
        >
          작업자
        </VCol>
        <VCol cols="10">
          <AppSelect
            v-model="filter.users"
            :items="users"
            item-title="name"
            item-value="user_id"
            multiple
            placeholder="전체"
            clearable
          />
        </VCol>
        <VCol
          cols="2"
          class="d-flex align-center"
        >
          수정일시
        </VCol>
        <VCol
          cols="10"
          class="d-flex align-center"
        >
          <AppDateTimePicker
            v-model="filter.startDate"
            append-inner-icon="tabler-calendar-event"
            style="inline-size:155px"
          />&nbsp;~&nbsp;
          <AppDateTimePicker
            v-model="filter.endDate"
            append-inner-icon="tabler-calendar-event"
            style="inline-size:155px"
          />
        </VCol>
      </VRow>
    </VCardText>
  </VCard>
  <VRow class="mt-2">
    <VCol>
      <VBtn @click="downloadExcel">
        Download<VIcon
          end
          icon="tabler-file-spreadsheet"
        />
      </VBtn>
    </VCol>
  </VRow>
</template>
